<script lang="ts">
  import { onDestroy } from 'svelte';
  import { getContacts, getConversations, startConversationWith } from './api';
  import type { Conversation, MessagingUser } from './api';
  import {
    getActivityComments,
    getDayComments,
    getMyThreads,
    markActivityCommentsRead,
    markDayCommentsRead,
    postActivityComment,
    postDayComment,
  } from '../Comments/api';
  import type { MyCommentThread } from '../Comments/api';
  import ConversationPane from './ConversationPane.svelte';
  import CommentThreadPanel from '../Comments/CommentThreadPanel.svelte';
  import ContactListItem from './ContactListItem.svelte';
  import EmptyState from './EmptyState.svelte';
  import ErrorBanner from './ErrorBanner.svelte';
  import Avatar from '../Avatar.svelte';
  import WhatsAppIcon from '../WhatsAppIcon.svelte';
  import { conversationTimeLabel } from './formatting';
  import { onActivateKeydown } from './interactions';
  import { Input } from '$lib/components/ui/input';
  import { Button } from '$lib/components/ui/button';
  import { Badge } from '$lib/components/ui/badge';
  import { Toggle } from '$lib/components/ui/toggle';
  import * as Select from '$lib/components/ui/select';
  import * as Tabs from '$lib/components/ui/tabs';

  // The centralized Messages page -- a single, recency-sorted inbox for every conversation and
  // comment thread the user has access to. Direct messages and comments are deliberately kept in
  // two entirely separate sections (not a shared list with a filter) -- a comment thread reads
  // nothing like a DM (no single "other participant", possibly several coaches, always tied to a
  // specific activity/day), so folding them into one list made that distinction easy to miss.
  // Reuses ConversationPane for the conversation detail view, and CommentThreadPanel (already
  // subject-agnostic) for the comment-thread detail view, same load/send/markRead closure wiring
  // ExerciseDialog.svelte/CalendarNotesModal.svelte already use. This is the only place
  // conversations surface in the UI -- there's no per-club/coach-page conversation list anymore.
  export let currentUserId: number;
  export let initialSelection: { kind: 'conversation' | 'thread'; id: string } | null = null;
  // Resolved server-side (MessagesController@getIndex) so the connect-WhatsApp banner is either
  // there or not from the first paint, rather than popping in after a status fetch.
  export let whatsappLinked: boolean = true;

  //@ts-ignore
  const Xert = window.Xert;

  type InboxRow = {
    key: string;
    kind: 'conversation' | 'thread';
    id: string;
    title: string;
    preview: string;
    timestamp: string | null;
    unreadCount: number;
    subjectId: number | null;
    subjectName: string | null;
    avatarUser: MessagingUser | null;
    // Only set for kind === 'thread'
    commentType: 'activity' | 'day' | null;
  };

  let conversations: Conversation[] = [];
  let threads: MyCommentThread[] = [];
  let loading = true;
  let error: string | null = null;
  let selectedKey: string | null = initialSelection ? `${initialSelection.kind}-${initialSelection.id}` : null;

  // Deep-linking into a comment thread (e.g. from a bell notification) should land on the Comments
  // section already showing it, not silently switch to Direct Messages underneath it -- but absent
  // that, Direct Messages is the sensible default (matches whichever channel most people use most).
  let activeSection: 'messages' | 'comments' = initialSelection?.kind === 'thread' ? 'comments' : 'messages';

  let search = '';
  let subjectFilter: number | null = null;
  let unreadOnly = false;

  // "New conversation" picker -- replaces the section tabs/filters/list with a contact search
  // while open, same swap-the-panel approach the old club-scoped ConversationSidebar used.
  let showPicker = false;
  let contacts: MessagingUser[] = [];
  let contactsLoaded = false;
  let contactSearch = '';

  // Hide contacts you already have a conversation with -- you'd just open the existing one.
  $: availableContacts = contacts.filter((contact) => !conversations.some((c) => c.participants.some((p) => p.id === contact.id)));
  $: filteredContacts = filterContacts(availableContacts, contactSearch);

  function filterContacts(list: MessagingUser[], query: string): MessagingUser[] {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) => `${c.first_name} ${c.last_name} ${c.username}`.toLowerCase().includes(q));
  }

  async function openPicker() {
    showPicker = true;
    if (contactsLoaded) return;
    try {
      contacts = await getContacts();
      contactsLoaded = true;
    } catch (e) {
      error = 'Unable to load contacts right now.';
      console.log(e);
    }
  }

  function closePicker() {
    showPicker = false;
    contactSearch = '';
  }

  async function startConversation(contact: MessagingUser) {
    try {
      const conversation = await startConversationWith(contact.id);
      if (!conversations.some((c) => c.id === conversation.id)) {
        conversations = [conversation, ...conversations];
      }
      closePicker();
      selectedKey = `conversation-${conversation.id}`;
    } catch (e) {
      error = 'Unable to start that conversation.';
      console.log(e);
    }
  }

  function conversationToRow(c: Conversation): InboxRow {
    const others = c.participants.filter((p) => p.id !== currentUserId);
    const primary = others[0] ?? null;
    return {
      key: `conversation-${c.id}`,
      kind: 'conversation',
      id: c.id,
      title: others.length ? others.map((p) => `${p.first_name} ${p.last_name}`).join(', ') : 'You',
      preview: c.last_message_preview ?? '',
      timestamp: c.last_message_at ?? null,
      unreadCount: c.unread_count ?? 0,
      subjectId: primary?.id ?? null,
      subjectName: primary ? `${primary.first_name} ${primary.last_name}` : null,
      avatarUser: primary,
      commentType: null,
    };
  }

  function threadToRow(t: MyCommentThread): InboxRow {
    return {
      key: `thread-${t.id}`,
      kind: 'thread',
      id: t.id,
      title: t.title,
      preview: t.last_comment_preview ?? '',
      timestamp: t.last_comment_at ?? null,
      unreadCount: t.unread_count,
      subjectId: t.subject?.id ?? null,
      subjectName: t.subject ? `${t.subject.first_name} ${t.subject.last_name}` : null,
      avatarUser: t.subject,
      commentType: t.type,
    };
  }

  $: conversationRows = conversations.map(conversationToRow).sort((a, b) => new Date(b.timestamp ?? 0).getTime() - new Date(a.timestamp ?? 0).getTime());
  $: threadRows = threads.map(threadToRow).sort((a, b) => new Date(b.timestamp ?? 0).getTime() - new Date(a.timestamp ?? 0).getTime());

  $: unreadMessageCount = conversations.reduce((sum, c) => sum + (c.unread_count ?? 0), 0);
  $: unreadCommentCount = threads.reduce((sum, t) => sum + t.unread_count, 0);

  $: sectionRows = activeSection === 'messages' ? conversationRows : threadRows;

  // Only worth showing once there's more than one distinct subject to choose between (a plain
  // athlete's own inbox never has more than themself) -- mainly useful for a coach narrowing down
  // to one athlete within whichever section they're looking at.
  $: subjectOptions = Array.from(new Map(sectionRows.filter((r) => r.subjectId != null).map((r) => [r.subjectId as number, r.subjectName as string])).entries()).sort((a, b) => a[1].localeCompare(b[1]));

  function selectSection(section: 'messages' | 'comments') {
    if (section === activeSection) return;
    activeSection = section;
  }

  function handleSectionChange(value: string | undefined) {
    if (value === 'messages' || value === 'comments') selectSection(value);
  }

  $: filteredRows = sectionRows.filter((row) => {
    if (unreadOnly && row.unreadCount === 0) return false;
    if (subjectFilter != null && row.subjectId !== subjectFilter) return false;
    const q = search.trim().toLowerCase();
    if (q && !`${row.title} ${row.preview}`.toLowerCase().includes(q)) return false;
    return true;
  });

  $: selectedRow = [...conversationRows, ...threadRows].find((r) => r.key === selectedKey) ?? null;
  $: selectedConversation = selectedRow?.kind === 'conversation' ? conversations.find((c) => c.id === selectedRow?.id) ?? null : null;
  $: selectedThread = selectedRow?.kind === 'thread' ? threads.find((t) => t.id === selectedRow?.id) ?? null : null;

  function selectRow(row: InboxRow) {
    selectedKey = row.key;
  }

  // Clears this row's badge in the left nav the moment it's actually read, instead of waiting on
  // an unrelated Pusher event to trigger a full refetch -- passed down as ConversationPane's/
  // CommentThreadPanel's onRead. (The navbar Messages icon's own badge is kept in sync by those
  // components themselves, not here -- see their notifyRead().)
  function markConversationReadLocally(conversationId: string) {
    conversations = conversations.map((c) => (c.id === conversationId ? { ...c, unread_count: 0 } : c));
  }

  function markThreadReadLocally(threadId: string) {
    threads = threads.map((t) => (t.id === threadId ? { ...t, unread_count: 0 } : t));
  }

  function updateConversationPreview(conversationId: string, preview: string, at: string) {
    conversations = conversations.map((c) => (c.id === conversationId ? { ...c, last_message_preview: preview, last_message_at: at } : c));
  }

  async function loadLists() {
    const [newConversations, newThreads] = await Promise.all([getConversations(), getMyThreads()]);
    conversations = newConversations;
    threads = newThreads;
  }

  async function init() {
    try {
      await loadLists();
    } catch (e) {
      error = 'Unable to load your messages right now.';
      console.log(e);
    } finally {
      loading = false;
    }
  }
  void init();

  // Keeps the merged list itself (previews/ordering/new items) live -- the open detail pane
  // (ConversationPane / CommentThreadPanel) already handles live-appending messages to itself.
  async function refreshLists() {
    try {
      await loadLists();
    } catch (e) {
      console.log(e);
    }
  }

  // Same event WhatsAppLink.svelte listens for -- linking finishes on the user's phone, not in
  // this tab, so the banner has to clear itself rather than wait for a reload.
  function onWhatsAppLinked() {
    whatsappLinked = true;
  }

  const channel = Xert._userChannel;
  if (channel) {
    channel.bind('message', refreshLists);
    channel.bind('comment', refreshLists);
    channel.bind('whatsapp_linked', onWhatsAppLinked);
  }
  onDestroy(() => {
    if (channel) {
      channel.unbind('message', refreshLists);
      channel.unbind('comment', refreshLists);
      channel.unbind('whatsapp_linked', onWhatsAppLinked);
    }
  });

  // index.blade.php only parses '#conversation-{id}'/'#thread-{id}' once, at initial page load
  // (into the initialSelection prop) -- clicking a notification link while already on this page
  // is a same-document navigation (no reload), so it never re-runs that script and the hash just
  // sits in the URL unhandled. This is the same parse/select/clear logic, run again on every
  // 'hashchange' so it also works once the page is already mounted.
  function selectFromHash() {
    const hash = window.location.hash;
    let selection: { kind: 'conversation' | 'thread'; id: string } | null = null;
    if (hash.indexOf('#conversation-') === 0) {
      selection = { kind: 'conversation', id: hash.replace('#conversation-', '') };
    } else if (hash.indexOf('#thread-') === 0) {
      selection = { kind: 'thread', id: hash.replace('#thread-', '') };
    }
    if (selection == null) return;

    history.replaceState(null, document.title, window.location.pathname);
    activeSection = selection.kind === 'thread' ? 'comments' : 'messages';
    selectedKey = `${selection.kind}-${selection.id}`;
  }

  window.addEventListener('hashchange', selectFromHash);
  onDestroy(() => window.removeEventListener('hashchange', selectFromHash));
</script>

<div class="twp flex h-[calc(100vh-46px)] w-full flex-col overflow-hidden bg-card">
  {#if error}
    <ErrorBanner message={error} onDismiss={() => (error = null)} />
  {/if}

  <div class="flex min-h-0 flex-1">
    <div class="flex w-full shrink-0 flex-col border-0 border-solid border-border md:w-[340px] md:border-r {selectedRow && 'hidden md:flex'}">
      {#if !whatsappLinked}
        <a
          href="/account/whatsapp"
          class="flex shrink-0 items-center gap-1.5 border-0 bg-[#25d366] px-4 py-1 text-[11px] font-medium leading-tight text-white no-underline hover:bg-[#22c35e] hover:text-white"
        >
          <span class="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-white">
            <WhatsAppIcon size={10} title="WhatsApp" />
          </span>
          <span class="min-w-0 flex-1 truncate">Message from your phone</span>
          <span class="shrink-0 rounded-full bg-white px-2 py-px text-[10px] font-semibold text-[#1da851]">Connect</span>
        </a>
      {/if}

      <div class="flex h-14 shrink-0 items-center justify-between border-b border-border px-4">
        <span class="text-[15px] font-semibold text-foreground">{showPicker ? 'New message' : 'Messages'}</span>
        {#if showPicker}
          <button type="button" class="text-sm text-muted-foreground hover:text-foreground" on:click={closePicker}>Cancel</button>
        {:else if activeSection === 'messages'}
          <Button variant="ghost" size="icon" class="shrink-0 text-muted-foreground hover:bg-primary/10 hover:text-primary" on:click={openPicker} title="Start a new conversation">
            <i class="fa-solid fa-pen-to-square text-sm"></i>
          </Button>
        {/if}
      </div>

      {#if showPicker}
        <div class="px-3 pb-2 pt-2">
          <Input type="search" placeholder="Search people…" bind:value={contactSearch} class="h-8" />
        </div>
        <div class="flex-1 overflow-y-auto">
          {#if !contactsLoaded}
            <div class="p-4 text-sm text-muted-foreground">Loading…</div>
          {:else if filteredContacts.length === 0}
            <EmptyState
              icon="fa-light fa-user-slash"
              description={availableContacts.length === 0 ? 'No one available to message right now.' : `No matches for "${contactSearch}".`}
              compact
            />
          {:else}
            {#each filteredContacts as contact (contact.id)}
              <ContactListItem {contact} collapsed={false} onSelect={() => startConversation(contact)} />
            {/each}
          {/if}
        </div>
      {:else}
      <Tabs.Root value={activeSection} onValueChange={handleSectionChange}>
        <Tabs.List class="grid h-11 w-full grid-cols-2 rounded-none border-0 border-b border-solid border-border bg-transparent p-0">
          <Tabs.Trigger value="messages" class="h-full gap-1.5 rounded-none border-0 border-b-2 border-solid border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none">
            Direct Messages
            {#if unreadMessageCount > 0}<Badge class="h-4 shrink-0 rounded-full px-1.5 text-[10px] leading-none">{unreadMessageCount}</Badge>{/if}
          </Tabs.Trigger>
          <Tabs.Trigger value="comments" class="h-full gap-1.5 rounded-none border-0 border-b-2 border-solid border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none">
            Comments
            {#if unreadCommentCount > 0}<Badge class="h-4 shrink-0 rounded-full px-1.5 text-[10px] leading-none">{unreadCommentCount}</Badge>{/if}
          </Tabs.Trigger>
        </Tabs.List>
      </Tabs.Root>

      <div class="flex flex-col gap-2 border-b border-border px-3 py-2">
        <Input type="search" placeholder="Search…" bind:value={search} class="h-8" />
        <div class="flex items-center gap-2">
          {#if subjectOptions.length > 1}
            <Select.Root
              portal="#shadcn-ui-dialog-target"
              selected={{ value: subjectFilter != null ? String(subjectFilter) : '', label: subjectFilter != null ? subjectOptions.find(([id]) => id === subjectFilter)?.[1] ?? '' : 'Everyone' }}
              onSelectedChange={(sel) => (subjectFilter = sel?.value ? Number(sel.value) : null)}
            >
              <Select.Trigger class="h-8 flex-1 text-xs">
                <Select.Value placeholder="Everyone" />
              </Select.Trigger>
              <Select.Content class="max-h-64 overflow-auto">
                <Select.Item value="">Everyone</Select.Item>
                {#each subjectOptions as [id, name] (id)}
                  <Select.Item value={String(id)}>{name}</Select.Item>
                {/each}
              </Select.Content>
            </Select.Root>
          {/if}
          <Toggle size="sm" variant="outline" class="h-8 shrink-0 gap-1.5 text-xs" bind:pressed={unreadOnly}>
            <i class="fa-solid fa-circle-dot text-[9px]"></i> Unread
          </Toggle>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto">
        {#if loading}
          <div class="p-4 text-sm text-muted-foreground">Loading…</div>
        {:else if filteredRows.length === 0}
          <EmptyState
            icon="fa-light fa-messages"
            description={sectionRows.length === 0
              ? activeSection === 'messages'
                ? 'No direct messages yet.'
                : 'No comment threads yet.'
              : 'No matches for your filters.'}
            compact
          />
        {:else}
          {#each filteredRows as row (row.key)}
            <div
              class="flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-accent {selectedKey === row.key ? 'bg-accent' : ''}"
              role="button"
              tabindex="0"
              on:click={() => selectRow(row)}
              on:keydown={(e) => onActivateKeydown(e, () => selectRow(row))}
            >
              {#if row.kind === 'conversation'}
                <div class="shrink-0">
                  {#if row.avatarUser}
                    <Avatar profile_photo={row.avatarUser.profile_photo ?? ''} first_name={row.avatarUser.first_name} last_name={row.avatarUser.last_name} username={row.avatarUser.username} shape="circle" pad_right={false} />
                  {:else}
                    <div class="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <i class="fa-solid fa-user text-xs"></i>
                    </div>
                  {/if}
                </div>
                <div class="min-w-0 flex-1">
                  <div class="flex items-baseline justify-between gap-2">
                    <span class="truncate text-sm font-medium text-foreground">{row.title}</span>
                    {#if row.timestamp}
                      <span class="shrink-0 text-[11px] text-muted-foreground">{conversationTimeLabel(row.timestamp)}</span>
                    {/if}
                  </div>
                  <div class="flex items-center justify-between gap-2">
                    {#if row.preview}
                      <p class="m-0 truncate text-xs text-muted-foreground">{row.preview}</p>
                    {:else}
                      <span></span>
                    {/if}
                    {#if row.unreadCount > 0}
                      <Badge class="h-4 shrink-0 rounded-full px-1.5 text-[10px] leading-none">{row.unreadCount}</Badge>
                    {/if}
                  </div>
                </div>
              {:else}
                <div class="relative shrink-0">
                  <div class="flex h-9 w-9 items-center justify-center rounded-md bg-muted text-muted-foreground">
                    <i class="fa-regular {row.commentType === 'activity' ? 'fa-bolt' : 'fa-calendar-day'} text-sm"></i>
                  </div>
                  {#if row.avatarUser && row.subjectId !== currentUserId}
                    <div class="absolute -bottom-1.5 -right-1.5 rounded-full ring-2 ring-card" style="--avatar-height: 1.1em;">
                      <Avatar profile_photo={row.avatarUser.profile_photo ?? ''} first_name={row.avatarUser.first_name} last_name={row.avatarUser.last_name} username={row.avatarUser.username} shape="circle" pad_right={false} />
                    </div>
                  {/if}
                </div>
                <div class="min-w-0 flex-1">
                  <div class="flex items-baseline justify-between gap-2">
                    <div class="flex min-w-0 items-center gap-1.5">
                      <span class="shrink-0 rounded bg-muted px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">{row.commentType === 'activity' ? 'Activity' : 'Day'}</span>
                      <span class="truncate text-sm font-medium text-foreground">{row.title}</span>
                    </div>
                    {#if row.timestamp}
                      <span class="shrink-0 text-[11px] text-muted-foreground">{conversationTimeLabel(row.timestamp)}</span>
                    {/if}
                  </div>
                  <div class="flex items-center justify-between gap-2">
                    {#if row.preview}
                      <p class="m-0 truncate text-xs text-muted-foreground">{row.subjectId !== currentUserId && row.subjectName ? `${row.subjectName}: ` : ''}{row.preview}</p>
                    {:else}
                      <span></span>
                    {/if}
                    {#if row.unreadCount > 0}
                      <Badge class="h-4 shrink-0 rounded-full px-1.5 text-[10px] leading-none">{row.unreadCount}</Badge>
                    {/if}
                  </div>
                </div>
              {/if}
            </div>
          {/each}
        {/if}
      </div>
      {/if}
    </div>

    <div class="min-h-0 min-w-0 flex-1 flex-col {selectedRow ? 'flex' : 'hidden md:flex'}">
      {#if selectedConversation}
        {#key selectedConversation.id}
          <ConversationPane
            conversation={selectedConversation}
            {currentUserId}
            allConversations={conversations}
            showBackButton={true}
            onBack={() => (selectedKey = null)}
            onActivity={updateConversationPreview}
            onError={(message) => (error = message)}
            onRead={markConversationReadLocally}
          />
        {/key}
      {:else if selectedThread}
        {#key selectedThread.id}
          <div class="flex shrink-0 flex-col gap-2 border-b border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div class="flex min-w-0 items-center gap-3">
              <Button variant="ghost" size="icon" class="shrink-0 md:hidden" on:click={() => (selectedKey = null)} title="Back">
                <i class="fa-solid fa-chevron-left text-xs"></i>
              </Button>
              <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                <i class="fa-regular {selectedThread.type === 'activity' ? 'fa-bolt' : 'fa-calendar-day'}"></i>
              </div>
              <div class="min-w-0 leading-tight">
                {#if selectedThread.type === 'activity'}
                  <!-- Same target as the "View activity" link opposite -- the title is what most
                  people try to click first, so it can't be the one dead spot in the header. -->
                  <a
                    href="/activities/{selectedThread.activity_path}"
                    target="_blank"
                    class="block truncate text-[15px] font-semibold text-foreground no-underline hover:text-primary hover:underline"
                  >
                    {selectedThread.title}
                  </a>
                {:else}
                  <div class="truncate text-[15px] font-semibold text-foreground">{selectedThread.title}</div>
                {/if}
                <div class="truncate text-xs text-muted-foreground">
                  {#if selectedThread.subject && selectedThread.subject.id !== currentUserId}
                    {selectedThread.subject.first_name} {selectedThread.subject.last_name}
                  {/if}
                </div>
              </div>
            </div>
            {#if selectedThread.type === 'activity'}
              <a href="/activities/{selectedThread.activity_path}" target="_blank" class="shrink-0 self-start text-xs font-medium text-primary hover:underline sm:self-auto">
                View activity <i class="fa-solid fa-arrow-up-right-from-square ml-1 text-[10px]"></i>
              </a>
            {/if}
          </div>
          <div class="flex min-h-0 flex-1 flex-col px-4 py-3">
            {#if selectedThread.type === 'activity'}
              <CommentThreadPanel
                fillHeight
                load={(before) => getActivityComments(selectedThread.activity_path, before)}
                send={(text, files, references, replyToId) => postActivityComment(selectedThread.activity_path, text, files, references, replyToId)}
                markRead={() => markActivityCommentsRead(selectedThread.activity_path)}
                onRead={() => markThreadReadLocally(selectedThread.id)}
              />
            {:else}
              <CommentThreadPanel
                fillHeight
                load={(before) => getDayComments(selectedThread.day, selectedThread.subject?.username, before)}
                send={(text, files, references, replyToId) => postDayComment(selectedThread.day, text, selectedThread.subject?.username, files, references, replyToId)}
                markRead={() => markDayCommentsRead(selectedThread.day, selectedThread.subject?.username)}
                onRead={() => markThreadReadLocally(selectedThread.id)}
              />
            {/if}
          </div>
        {/key}
      {:else}
        <EmptyState icon="fa-light fa-messages" description={activeSection === 'messages' ? 'Select a conversation to view it.' : 'Select a comment thread to view it.'} />
      {/if}
    </div>
  </div>
</div>
